/**
 * @fileoverview Premium/Freemium management module
 * Handles premium status, unlock codes, and Stripe checkout
 */

import { StorageService } from "./StorageService";


const STORAGE_KEY = 'toddlerGame_premium';
const UNLOCK_CODE_KEY = 'toddlerGame_unlockCode';

export interface PremiumStatus {
    isPremiumUser: boolean;
    unlockCode?: string;
    unlockedAt?: number;
}

export class PremiumService {
    /**
     * Check if user has premium access
     */
    static checkPremiumStatus(): PremiumStatus {
        try {
            const stored = localStorage.getItem(STORAGE_KEY);
            if (stored) {
                return JSON.parse(stored);
            }
        } catch (e) {
            console.warn('Failed to load premium status:', e);
        }
        
        return { isPremiumUser: false };
    }

    /**
     * Save premium status to localStorage
     */
    static savePremiumStatus(status: PremiumStatus): void {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify(status));
        } catch (e) {
            console.warn('Failed to save premium status:', e);
        }
    }

    /**
     * Validate unlock code with backend
     */
    static async validateUnlockCode(code: string): Promise<boolean> {
        try {
            const response = await fetch('/.netlify/functions/validate-code', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ code }),
            });

            const data = await response.json();
            
            if (data.valid) {
                // Save premium status
                this.savePremiumStatus({
                    isPremiumUser: true,
                    unlockCode: code,
                    unlockedAt: Date.now(),
                });
                return true;
            }
            
            return false;
        } catch (error) {
            console.error('Failed to validate unlock code:', error);
            return false;
        }
    }

    /**
     * Initialize Stripe checkout process
     */
    static async initStripeCheckout(): Promise<void> {
        // Development mode mock (when Netlify functions aren't available)
        const isDevelopment = window.location.hostname === 'localhost' || 
                             window.location.hostname === '127.0.0.1';
        
        if (isDevelopment && import.meta.env.DEV) {
            console.log('🧪 DEV MODE: Mocking Stripe checkout');
            const mockPurchase = confirm(
                '🧪 DEVELOPMENT MODE\n\n' +
                'Simulate premium purchase?\n\n' +
                'Click OK to unlock premium (test mode)\n' +
                'Click Cancel to abort'
            );
            
            if (mockPurchase) {
                // Generate a fake unlock code
                const mockCode = 'DEV-TEST-MOCK-' + Math.random().toString(36).substring(2, 6).toUpperCase();
                
                // Save premium status
                this.savePremiumStatus({
                    isPremiumUser: true,
                    unlockCode: mockCode,
                    unlockedAt: Date.now(),
                });
                
                alert(`🎉 Premium unlocked (DEV MODE)!\n\nMock unlock code: ${mockCode}\n\nReloading to show premium themes...`);
                window.location.reload();
                return;
            }
            // reset storage and reload to reset themes
            StorageService.getInstance().clearSavedConfiguration();
            return;

        }
        
        try {
            // Call backend to create checkout session
            const response = await fetch('/.netlify/functions/create-checkout', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
            });

            const { url } = await response.json();
            
            if (url) {
                // Redirect to Stripe Checkout
                window.location.href = url;
            } else {
                throw new Error('No checkout URL received');
            }
        } catch (error) {
            console.error('Failed to initialize checkout:', error);
            alert('Failed to start payment process. Please try again.');
        }
    }

    /**
     * Handle payment success (called after redirect from Stripe)
     */
    static async handlePaymentSuccess(sessionId: string): Promise<string | null> {
        try {
            // Verify payment and get unlock code
            const response = await fetch('/.netlify/functions/verify-payment', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ sessionId }),
            });

            const data = await response.json();
            
            if (data.unlockCode) {
                // Save premium status
                this.savePremiumStatus({
                    isPremiumUser: true,
                    unlockCode: data.unlockCode,
                    unlockedAt: Date.now(),
                });
                
                return data.unlockCode;
            }
            
            return null;
        } catch (error) {
            console.error('Failed to verify payment:', error);
            return null;
        }
    }

    /**
     * Reset premium status (for testing)
     */
    static resetPremium(): void {
        localStorage.removeItem(STORAGE_KEY);
        localStorage.removeItem(UNLOCK_CODE_KEY);
    }
}
